import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Inbox, Plus, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, PriorityBadge, StatusBadge, EmptyState, Card } from "@/components/common";
import { relativeTime } from "@/lib/format";

export default function Directives() {
  const { companyParam, companyId, setDirectiveOpen, companies } = useApp();
  const navigate = useNavigate();
  const { data: directives = [], isLoading } = useQuery({
    queryKey: ["directives", companyId],
    queryFn: async () => (await api.get("/directives", { params: companyParam })).data,
  });

  const coName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="directives-page">
      <PageHeader
        title="Directive Inbox"
        subtitle="Submit large requests in a structured format. Agents classify, route, build the task tree, and prepare an approval package."
        actions={
          <button onClick={() => setDirectiveOpen(true)} data-testid="page-new-directive-btn" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-dark transition-colors">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> New Directive
          </button>
        }
      />

      {isLoading ? (
        <div className="text-sm text-ink-muted">Loading…</div>
      ) : directives.length === 0 ? (
        <EmptyState icon={Inbox} title="No directives yet" hint="Submit your first directive to see the autonomous pipeline in action." />
      ) : (
        <Card>
          <div className="divide-y divide-border">
            {directives.map((d) => (
              <button
                key={d.id}
                onClick={() => d.job_id && navigate(`/jobs/${d.job_id}`)}
                data-testid={`directive-row-${d.id}`}
                className="w-full grid grid-cols-12 gap-3 items-center px-4 py-3.5 text-left hover:bg-surface-muted transition-colors group"
              >
                <div className="col-span-5">
                  <div className="text-sm font-semibold text-ink leading-snug">{d.goal}</div>
                  <div className="text-xs text-ink-muted mt-0.5">{coName(d.company_id)} · {d.client || "Internal"} · {d.project || "—"}</div>
                </div>
                <div className="col-span-2 text-xs text-ink-muted">{d.classification?.task_label || d.job_type || "—"}</div>
                <div className="col-span-1"><PriorityBadge value={d.classification?.priority || d.priority || "medium"} /></div>
                <div className="col-span-2"><StatusBadge value={d.status} /></div>
                <div className="col-span-1 text-[11px] font-mono text-ink-faint">{relativeTime(d.created_at)}</div>
                <div className="col-span-1 flex justify-end">
                  <ArrowRight className="w-4 h-4 text-ink-faint group-hover:text-brand group-hover:translate-x-0.5 transition-all" />
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
